export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080').replace(/\/$/, '')

export type UserSummary = {
  id: string
  staffId: string | null
  username: string
  displayName: string
  passwordChangeRequired: boolean
  root: boolean
  roles: string[]
  permissions: string[]
  storeIds: string[]
}

export type AuthSession = {
  tokenType: string
  accessToken: string
  accessTokenExpiresAt: string
  refreshToken: string
  refreshTokenExpiresAt: string
  user: UserSummary
}

export type StoreRecord = {
  id: string
  code: string
  name: string
  address: string | null
  latitude: number | null
  longitude: number | null
  geofenceRadiusMeters: number
  active: boolean
  receiptSettings: ReceiptSettings
}

export type ReceiptSettings = {
  headerName: string | null
  registrationNo: string | null
  phone: string | null
  email: string | null
  sstId: string | null
  language: 'ENGLISH' | 'CHINESE'
  taxRatePercent: number
  serviceChargeRatePercent: number
  footer: string | null
}

export type RoomRecord = { id: string; storeId: string; name: string; active: boolean; maxConcurrentOrders: number }
export type PackageRecord = { id: string; storeId: string; name: string; durationMinutes: number; price: number; active: boolean }
export type OrderTypeRecord = { id: string; storeId: string; code: string; name: string; sortOrder: number; active: boolean }
export type DurationRecord = { id: string; storeId: string; minutes: number; label: string; sortOrder: number; active: boolean }

export type CommissionRuleRecord = {
  id: string
  storeId: string
  orderTypeId: string
  version: number
  recipientRole: 'DUTY_MANAGER' | 'CUSTOMER_MANAGER' | 'GRO' | 'SUPERVISOR' | 'ROOM_FEE'
  method: 'PERCENTAGE' | 'FIXED_PER_ORDER' | 'FIXED_PER_HOUR'
  rate: number
  effectiveFrom: string
  active: boolean
}

export type StaffRecord = {
  id: string
  employeeNo: string
  displayName: string
  phone: string | null
  supervisorId: string | null
  active: boolean
  serviceStatus: 'IDLE' | 'IN_SERVICE' | 'OFF_DUTY'
  storeIds: string[]
  tags: string[]
  avatarFileId: string | null
}

export type OrderRecord = {
  id: string
  orderNo: string
  storeId: string
  roomId: string
  orderTypeId: string
  dutyManagerId: string
  customerManagerId: string
  groId: string
  status: 'WAITING' | 'IN_SERVICE' | 'AWAITING_SETTLEMENT' | 'COMPLETED' | 'CLOSED'
  agreedDurationMinutes: number
  actualDurationMinutes: number | null
  agreedAmount: number
  finalAmount: number | null
  paymentMethod: 'CASH' | 'CARD' | 'ONLINE' | 'OTHER'
  currency: string
  startedAt: string | null
  expectedEndAt: string | null
  serviceEndedAt: string | null
  settledAt: string | null
  version: number
  packageId: string | null
  packagePrice: number | null
  packageDurationMinutes: number | null
  storeName: string
  roomName: string
  orderTypeName: string
  packageName: string | null
  dutyManagerName: string
  customerManagerName: string
  groName: string
  createdAt: string
  closedAt: string | null
  adjustmentReason: string | null
  closeReason: string | null
  settlementPreview: SettlementPreviewRecord | null
}

export type CommissionEstimateRecord = {
  snapshotId: string
  recipientRole: CommissionRuleRecord['recipientRole']
  recipientId: string | null
  recipientName: string | null
  method: CommissionRuleRecord['method']
  rate: number
  ruleVersion: number
  estimatedAmount: number
  settledAmount: number | null
}

export type SettlementPreviewRecord = {
  settlementDurationMinutes: number
  settlementAmount: number
  estimatedCommissionTotal: number
  commissionLines: CommissionEstimateRecord[]
}

export type PageRecord<T> = {
  content: T[]
  totalElements: number
  totalPages: number
  number: number
  size: number
}

export type ShiftRecord = { id: string; storeId: string; staffId: string; startsAt: string; endsAt: string; clockInEarlyMinutes: number; clockInLateMinutes: number; clockOutEarlyMinutes: number; clockOutLateMinutes: number }
export type AttendanceRecord = {
  id: string
  shiftId: string
  staffId: string
  clockedInAt: string | null
  clockedOutAt: string | null
  clockInLatitude: number | null
  clockInLongitude: number | null
  clockInAccuracyMeters: number | null
  clockInCapturedAt: string | null
  clockOutLatitude: number | null
  clockOutLongitude: number | null
  clockOutAccuracyMeters: number | null
  clockOutCapturedAt: string | null
  status: 'NORMAL' | 'LATE' | 'EARLY_LEAVE' | 'ABSENT' | 'LEAVE' | 'CORRECTED'
  correctionReason: string | null
}

export type PerformanceEntryRecord = {
  id: string
  orderId: string
  staffId: string | null
  role: string
  amount: number
  recognizedAt: string
}

export type MonthCloseLineRecord = {
  id: string
  staffId: string
  grossAmount: number
  deduction: number
  deductionNote: string | null
  netAmount: number
}

export type MonthCloseRecord = {
  id: string
  storeId: string
  period: string
  status: 'DRAFT' | 'PUBLISHED'
  publishedAt: string | null
  lines: MonthCloseLineRecord[]
}

export type AuditRecord = {
  id: string
  createdAt: string
  updatedAt: string
  actor: string
  action: string
  entityType: string
  entityId: string
  details: string | null
  ipAddress: string | null
}

export type NotificationRecord = {
  id: string
  storeId: string
  type: string
  title: string
  content: string
  entityType: string | null
  entityId: string | null
  createdAt: string
  readAt: string | null
}

export type RoleRecord = { id: string; code: string; name: string; permissions: string[] }
export type UserAccountRecord = {
  id: string
  staffId: string | null
  username: string
  displayName: string
  active: boolean
  passwordChangeRequired: boolean
  roles: string[]
  storeIds: string[]
  permissionOverrides: Record<string, 'GRANT' | 'DENY'>
  initialTemporaryPassword: string | null
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) { super(message) }
}

const readProblem = async (response: Response) => {
  const fallback = `请求失败 (${response.status})`
  try {
    const body = await response.json() as { detail?: string; title?: string; code?: string }
    return new ApiError(body.detail ?? body.title ?? fallback, response.status, body.code)
  } catch { return new ApiError(fallback, response.status) }
}

const query = (values: Record<string, string | number | undefined>) => {
  const params = new URLSearchParams()
  Object.entries(values).forEach(([key, value]) => { if (value !== undefined) params.set(key, String(value)) })
  return params.toString()
}

export class StoreOpsApi {
  constructor(readonly baseUrl = API_BASE_URL) {}

  private async request<T>(path: string, init: RequestInit = {}, accessToken?: string): Promise<T> {
    let response: Response
    try {
      response = await fetch(`${this.baseUrl}${path}`, {
        ...init,
        headers: {
          Accept: 'application/json',
          ...(init.body && !(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          ...init.headers,
        },
      })
    } catch { throw new ApiError(`无法连接 Java 服务 (${this.baseUrl})`, 0, 'NETWORK_ERROR') }
    if (!response.ok) throw await readProblem(response)
    if (response.status === 204) return undefined as T
    return response.json() as Promise<T>
  }

  login(username: string, password: string) { return this.request<AuthSession>('/api/v1/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }) }
  refresh(refreshToken: string) { return this.request<AuthSession>('/api/v1/auth/refresh', { method: 'POST', body: JSON.stringify({ refreshToken }) }) }
  logout(refreshToken: string) { return this.request<void>('/api/v1/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken }) }) }
  changePassword(token: string, currentPassword: string, newPassword: string) { return this.request<AuthSession>('/api/v1/auth/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) }, token) }

  stores(token: string) { return this.request<StoreRecord[]>('/api/v1/stores', {}, token) }
  saveStore(token: string, value: Omit<StoreRecord, 'id'>, id?: string) { return this.request<StoreRecord>(id ? `/api/v1/stores/${id}` : '/api/v1/stores', { method: id ? 'PUT' : 'POST', body: JSON.stringify(value) }, token) }
  rooms(token: string, storeId: string) { return this.request<RoomRecord[]>(`/api/v1/rooms?${query({ storeId })}`, {}, token) }
  saveRoom(token: string, value: Omit<RoomRecord, 'id'>, id?: string) { return this.request<RoomRecord>(id ? `/api/v1/rooms/${id}` : '/api/v1/rooms', { method: id ? 'PUT' : 'POST', body: JSON.stringify(value) }, token) }
  packages(token: string, storeId: string) { return this.request<PackageRecord[]>(`/api/v1/packages?${query({ storeId })}`, {}, token) }
  savePackage(token: string, value: Omit<PackageRecord, 'id'>, id?: string) { return this.request<PackageRecord>(id ? `/api/v1/packages/${id}` : '/api/v1/packages', { method: id ? 'PUT' : 'POST', body: JSON.stringify(value) }, token) }
  orderTypes(token: string, storeId: string) { return this.request<OrderTypeRecord[]>(`/api/v1/order-types?${query({ storeId })}`, {}, token) }
  durations(token: string, storeId: string) { return this.request<DurationRecord[]>(`/api/v1/duration-options?${query({ storeId })}`, {}, token) }
  saveOrderType(token: string, value: Omit<OrderTypeRecord, 'id'>, id?: string) { return this.request<OrderTypeRecord>(id ? `/api/v1/order-types/${id}` : '/api/v1/order-types', { method: id ? 'PUT' : 'POST', body: JSON.stringify(value) }, token) }
  saveDuration(token: string, value: Omit<DurationRecord, 'id'>, id?: string) { return this.request<DurationRecord>(id ? `/api/v1/duration-options/${id}` : '/api/v1/duration-options', { method: id ? 'PUT' : 'POST', body: JSON.stringify(value) }, token) }

  staff(token: string, storeId?: string) { return this.request<StaffRecord[]>(`/api/v1/staff${storeId ? `?${query({ storeId })}` : ''}`, {}, token) }
  saveStaff(token: string, value: { employeeNo: string; displayName: string; phone: string | null; active: boolean; storeIds: string[]; tags: string[]; supervisorId: string | null }, id?: string) { return this.request<StaffRecord>(id ? `/api/v1/staff/${id}` : '/api/v1/staff', { method: id ? 'PUT' : 'POST', body: JSON.stringify(value) }, token) }
  uploadStaffAvatar(token: string, staffId: string, file: File) {
    const body = new FormData(); body.append('file', file)
    return this.request<{ fileId: string }>(`/api/v1/staff/${staffId}/avatar`, { method: 'PUT', body }, token)
  }
  async staffAvatar(token: string, staffId: string) {
    const response = await fetch(`${this.baseUrl}/api/v1/staff/${staffId}/avatar`, { headers: { Authorization: `Bearer ${token}` } })
    if (!response.ok) throw await readProblem(response)
    return response.blob()
  }

  orders(token: string, storeId: string, page = 0, size = 100, filters: { status?: OrderRecord['status']; staffId?: string; from?: string; to?: string } = {}) { return this.request<PageRecord<OrderRecord>>(`/api/v1/orders?${query({ storeId, page, size, ...filters })}`, {}, token) }
  async allOrders(token: string, storeId: string, filters: { status?: OrderRecord['status']; staffId?: string; from?: string; to?: string } = {}) {
    const content: OrderRecord[] = []
    let page = 0
    let result: PageRecord<OrderRecord>
    do {
      result = await this.orders(token, storeId, page, 100, filters)
      content.push(...result.content)
      page += 1
    } while (page < result.totalPages)
    return { ...result, content, number: 0, size: content.length }
  }
  createOrder(token: string, value: { storeId: string; roomId: string; orderTypeId: string; packageId: string | null; dutyManagerId: string; customerManagerId: string; groId: string; durationMinutes: number; amount: number; paymentMethod: OrderRecord['paymentMethod'] }, idempotencyKey: string) { return this.request<OrderRecord>('/api/v1/orders', { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(value) }, token) }
  orderAction(token: string, id: string, action: 'start' | 'finish', idempotencyKey: string) { return this.request<OrderRecord>(`/api/v1/orders/${id}/${action}`, { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey } }, token) }
  extendOrder(token: string, id: string, value: { minutes: number; amount: number; reason: string }, idempotencyKey: string) { return this.request<OrderRecord>(`/api/v1/orders/${id}/extend`, { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(value) }, token) }
  settlementPreview(token: string, id: string, proposed?: { finalDurationMinutes: number; finalAmount: number }) { return this.request<OrderRecord>(`/api/v1/orders/${id}/settlement-preview${proposed ? `?${query(proposed)}` : ''}`, {}, token) }
  settleOrder(token: string, id: string, value: { finalDurationMinutes?: number; finalAmount?: number; reason?: string }, idempotencyKey: string) { return this.request<OrderRecord>(`/api/v1/orders/${id}/settle`, { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify(value) }, token) }
  closeOrder(token: string, id: string, reason: string, idempotencyKey: string) { return this.request<OrderRecord>(`/api/v1/orders/${id}/close`, { method: 'POST', headers: { 'Idempotency-Key': idempotencyKey }, body: JSON.stringify({ reason }) }, token) }

  shifts(token: string, storeId: string, from: string, to: string) { return this.request<ShiftRecord[]>(`/api/v1/shifts?${query({ storeId, from, to })}`, {}, token) }
  createShift(token: string, value: { storeId: string; staffId: string; startsAt: string; endsAt: string; clockInEarlyMinutes: number; clockInLateMinutes: number; clockOutEarlyMinutes: number; clockOutLateMinutes: number }) { return this.request<ShiftRecord>('/api/v1/shifts', { method: 'POST', body: JSON.stringify(value) }, token) }
  attendance(token: string, staffId: string) { return this.request<AttendanceRecord[]>(`/api/v1/attendance?${query({ staffId })}`, {}, token) }
  correctAttendance(token: string, id: string, status: AttendanceRecord['status'], reason: string) { return this.request<AttendanceRecord>(`/api/v1/attendance/${id}/correction`, { method: 'PUT', body: JSON.stringify({ status, reason }) }, token) }

  performanceEntries(token: string, staffId: string, from: string, to: string) { return this.request<PerformanceEntryRecord[]>(`/api/v1/performance/entries?${query({ staffId, from, to })}`, {}, token) }
  findMonthClose(token: string, storeId: string, period: string) { return this.request<MonthCloseRecord>(`/api/v1/performance/month-close?${query({ storeId, period })}`, {}, token) }
  draftMonthClose(token: string, storeId: string, period: string) { return this.request<MonthCloseRecord>(`/api/v1/performance/month-close?${query({ storeId, period })}`, { method: 'POST' }, token) }
  monthClose(token: string, id: string) { return this.request<MonthCloseRecord>(`/api/v1/performance/month-close/${id}`, {}, token) }
  updateMonthCloseLine(token: string, closeId: string, lineId: string, deduction: number, note: string) { return this.request<MonthCloseLineRecord>(`/api/v1/performance/month-close/${closeId}/lines/${lineId}`, { method: 'PUT', body: JSON.stringify({ deduction, note }) }, token) }
  publishMonthClose(token: string, id: string) { return this.request<MonthCloseRecord>(`/api/v1/performance/month-close/${id}/publish`, { method: 'POST' }, token) }

  commissionRules(token: string, storeId: string) { return this.request<CommissionRuleRecord[]>(`/api/v1/commission-rules?${query({ storeId })}`, {}, token) }
  createCommissionRule(token: string, value: { storeId: string; orderTypeId: string; recipientRole: CommissionRuleRecord['recipientRole']; method: CommissionRuleRecord['method']; rate: number }) { return this.request<CommissionRuleRecord>('/api/v1/commission-rules', { method: 'POST', body: JSON.stringify(value) }, token) }
  adminRoles(token: string) { return this.request<RoleRecord[]>('/api/v1/admin/roles', {}, token) }
  saveRole(token: string, value: { code: string; name: string; permissions: string[] }, id?: string) { return this.request<RoleRecord>(id ? `/api/v1/admin/roles/${id}` : '/api/v1/admin/roles', { method: id ? 'PUT' : 'POST', body: JSON.stringify(value) }, token) }
  adminUsers(token: string) { return this.request<UserAccountRecord[]>('/api/v1/admin/users', {}, token) }
  saveUser(token: string, value: { username: string; displayName: string; staffId: string | null; temporaryPassword: string | null; active: boolean; roleIds: string[]; storeIds: string[]; permissionOverrides: Record<string, 'GRANT' | 'DENY'> }, id?: string) { return this.request<UserAccountRecord>(id ? `/api/v1/admin/users/${id}` : '/api/v1/admin/users', { method: id ? 'PUT' : 'POST', body: JSON.stringify(value) }, token) }
  audit(token: string, page = 0, size = 50) { return this.request<PageRecord<AuditRecord>>(`/api/v1/audit?${query({ page, size })}`, {}, token) }
  notifications(token: string, staffId: string) { return this.request<NotificationRecord[]>(`/api/v1/notifications?${query({ staffId })}`, {}, token) }
  readNotification(token: string, id: string) { return this.request<NotificationRecord>(`/api/v1/notifications/${id}/read`, { method: 'POST' }, token) }
}

export const api = new StoreOpsApi()
